import { useQuery } from '@tanstack/react-query'
import { apiService } from '@/services/api'
import type { ChiefAccessState } from '@/types/chief'

export interface UseChiefAccessResult {
  /** What the UI should show. See ChiefAccessState on the server. */
  state: ChiefAccessState;
  /** Convenience for the many callers that only care whether Chief is usable. */
  hasAccess: boolean;
  /**
   * Whether the Recommendation Report is really available to this account.
   *
   * Being enrolled in Chief is NOT enough: sampling sits behind its own
   * server-side allow-list. The briefing used to link every Chief user to a
   * page whose API answered 403, so this is reported alongside state and must
   * gate any entry point into that feature.
   */
  recoEnabled: boolean;
  isLoading: boolean;
}

interface ChiefAccessProbe {
  state: ChiefAccessState;
  recoEnabled: boolean;
}

const VALID_STATES: readonly ChiefAccessState[] = [
  'enrolled',
  'eligible',
  'needs_owner',
  'ineligible',
  'hidden',
]

/**
 * Hook to check whether the current account may use Chief of Staff, and if
 * not, why not: the UI has to distinguish four different "no" answers (show
 * an opt-in button, show "ask your account owner", show an upgrade link, or
 * show nothing at all), so this reports the full state, not just a boolean.
 *
 * ONE way in, matching the server gate (requireChiefAccess): probe
 * GET /chief/access. There is deliberately no admin short-circuit here. This
 * hook used to resolve every platform admin straight to 'enrolled' without a
 * request, which mirrored a server-side admin door that no longer exists, and
 * meant an admin could never see any state a customer sees. Removing it is what
 * makes the landing page reachable by the people who maintain it.
 *
 * That endpoint exists precisely for this hook: it is the one chief route that
 * sits above requireChiefAccess, it resolves access and nothing else, and it
 * answers 200 either way. Do NOT repoint this at the briefing. Layout renders on
 * every authenticated page, so this probe runs constantly, and the briefing fans
 * out across many queries plus an LLM narration call. A transport error (the
 * endpoint is unreachable, the session is dead), or an unrecognised/missing
 * state, fails closed to 'hidden' -- never to 'eligible' -- matching the server
 * resolver: a malformed answer must not read as an invitation to opt in to
 * something we could not confirm this account may have.
 *
 * The probe is cached under the 'chief-access' key with a 5 minute staleTime so
 * navigating around the app does not add a request per page. Entitlement is per
 * account (teamId ?? userId), and the key is deliberately prefixed 'chief-' so
 * the team switch handler in useTeam.ts, which invalidates every key starting
 * with 'chief-', clears this answer when the active team changes.
 */
export function useChiefAccess(): UseChiefAccessResult {
  const { data } = useQuery<ChiefAccessProbe>({
    queryKey: ['chief-access'],
    queryFn: async () => {
      try {
        const response = await apiService.getChiefAccess()
        const state = response.data?.state
        // An unrecognised or missing state fails closed to 'hidden', never to
        // 'eligible'. A malformed answer must not read as an invitation to opt
        // in to something we could not confirm this account may have.
        // recoEnabled fails closed the same way, and only an explicit true
        // counts: a server too old to send the field must not read as a yes.
        return {
          state: VALID_STATES.includes(state as ChiefAccessState)
            ? (state as ChiefAccessState)
            : 'hidden',
          recoEnabled: response.data?.recoEnabled === true,
        }
      } catch {
        return { state: 'hidden', recoEnabled: false }
      }
    },
    staleTime: 1000 * 60 * 5,
    retry: false
  })

  // data stays undefined until the probe resolves. Reporting 'hidden' while
  // loading is what keeps the nav item and the route from flashing in and out
  // for an account that turns out not to have Chief.
  if (data === undefined) {
    return { state: 'hidden', hasAccess: false, recoEnabled: false, isLoading: true }
  }

  return {
    state: data.state,
    hasAccess: data.state === 'enrolled',
    recoEnabled: data.recoEnabled,
    isLoading: false,
  }
}
